/**
 * Date parsing for extracted values (spec §8.1 #5). Date-only values are read
 * from their calendar fields — never through `new Date(str)` — so they can't
 * slip a day. Real timestamps go through the shared `formatDateTime` (Eastern),
 * per the repo's date convention.
 */
import { formatDateTime } from "../../../../../../../_src_shared/utils/functions/formatDateTime";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MON_IDX = Object.fromEntries(MON.map((m, i) => [m.toLowerCase(), i + 1]));

const valid = (y, m, d) => y > 1000 && y < 3000 && m >= 1 && m <= 12 && d >= 1 && d <= 31;
const year2 = (yy) => (yy <= 49 ? 2000 + yy : 1900 + yy);

/**
 * @returns {{ kind: "date", y, m, d } | { kind: "datetime", iso, y, m, d } | null}
 */
export const parseDateLike = (value) => {
  if (typeof value !== "string") return null;
  const s = value.trim();
  if (!s || s.length > 40) return null;
  let m;
  if ((m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/))) {
    const [y, mo, d] = [+m[1], +m[2], +m[3]];
    return valid(y, mo, d) ? { kind: "date", y, m: mo, d } : null;
  }
  if (
    (m = s.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/,
    ))
  ) {
    const [y, mo, d] = [+m[1], +m[2], +m[3]];
    if (!valid(y, mo, d)) return null;
    const midnight = m[4] === "00" && m[5] === "00" && (!m[6] || m[6] === "00");
    return midnight ? { kind: "date", y, m: mo, d } : { kind: "datetime", iso: s, y, m: mo, d };
  }
  if ((m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/))) {
    const y = m[3].length === 2 ? year2(+m[3]) : +m[3];
    return valid(y, +m[1], +m[2]) ? { kind: "date", y, m: +m[1], d: +m[2] } : null;
  }
  if ((m = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/))) {
    return valid(+m[1], +m[2], +m[3]) ? { kind: "date", y: +m[1], m: +m[2], d: +m[3] } : null;
  }
  if ((m = s.match(/^([A-Za-z]{3})[a-z]*\.?\s+(\d{1,2}),?\s+(\d{4})$/))) {
    const mo = MON_IDX[m[1].toLowerCase()];
    return mo && valid(+m[3], mo, +m[2]) ? { kind: "date", y: +m[3], m: mo, d: +m[2] } : null;
  }
  if ((m = s.match(/^(\d{1,2})\s+([A-Za-z]{3})[a-z]*\.?,?\s+(\d{4})$/))) {
    const mo = MON_IDX[m[2].toLowerCase()];
    return mo && valid(+m[3], mo, +m[1]) ? { kind: "date", y: +m[3], m: mo, d: +m[1] } : null;
  }
  return null;
};

export const displayDate = ({ y, m, d }) => `${MON[m - 1]} ${d}, ${y}`;

export const isoDate = ({ y, m, d }) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export const displayDateTime = (iso) => {
  const out = formatDateTime(iso, "MMM d, yyyy, h:mm a");
  return out ? `${out} ET` : iso;
};

/** Calendar fields for "now" (UTC — the day boundary is irrelevant at month scale). */
export const nowFields = (now) => ({
  y: now.getUTCFullYear(),
  m: now.getUTCMonth() + 1,
  d: now.getUTCDate(),
});

const dayKey = ({ y, m, d }) => y * 10000 + m * 100 + d;

export const isBefore = (a, b) => dayKey(a) < dayKey(b);

/** Whole months from `a` to `b` (b later), floor. */
export const monthsBetween = (a, b) => {
  let n = (b.y - a.y) * 12 + (b.m - a.m);
  if (b.d < a.d) n -= 1;
  return n;
};

export const yearsBetween = (a, b) => Math.floor(monthsBetween(a, b) / 12);

/** Add days to a date (calendar math in UTC). */
export const addDays = ({ y, m, d }, days) => {
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
};

export const agoText = (months) => {
  if (months < 1) return "less than a month ago";
  if (months < 24) return `${months} ${months === 1 ? "month" : "months"} ago`;
  const y = Math.floor(months / 12);
  return `${y} years ago`;
};
